import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    ActionButton,
    DetailsList,
    IDetailsGroupDividerProps,
    IDetailsHeaderProps,
    IGroup,
    IRenderFunction,
    IDetailsRowProps,
    useTheme,
    Selection,
    SelectionMode,
    Shimmer,
    Stack,
    IColumn,
    TooltipHost,
    Icon,
} from '@fluentui/react';
import {IWorkspaceItem, IWorkspaceItemGroupSortUpdate} from "../../models/Workspace";
import {
    useGetColumnsQuery,
    useGetWorkspaceProductsQuery
} from "../../store/Api";

import {
    useConvertUserAddedItemMutation,
    usePutWorkspaceItemDetailsMutation,
    useRemoveProductFromWorkspaceMutation,
    useUpdateWorkspaceItemGroupingMutation, 
    useUpdateWorkspaceItemGroupingSortMutation
} from "../../store/endpoints/WorkspaceItemsEndpoints";

import {renderWorkspaceColumns} from "../../logic/Columns/ProductColumns";
import {WORKSPACECOLUMNS} from "../../Constants";
import Header from "../Common/Header";
import {toast} from "react-toastify";
import BundlePeek from "./BundlePeek";
import {usePersistedColumnLayout} from "../../hooks/usePersistedColumnLayout";
import {openGlobalSearch} from "../GlobalSearch/useGlobalSearchHotkey";
import Sticky from "react-stickynode";
import {mergeStyles} from "@fluentui/react/lib/Styling";
import {BUNDLE_DRAG_TYPE} from "../ProductBundles/ProductBundleWorkspaceTable";
import WorkspaceGroupDialog from "./WorkspaceGroupDialog";
import {forEach} from "lodash";

// ---- Palette of group colors (left border + tinted header background) ----
const GROUP_COLORS = [
    '#0078d4', // blue
    '#107c10', // green
    '#a4262c', // red
    '#ca5010', // orange
    '#8764b8', // purple
    '#038387', // teal
    '#c19c00', // gold
    '#e3008c', // magenta
];

/** Map group names → stable color index using a simple hash so the color persists within a session. */
function colorForGroup(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return GROUP_COLORS[hash % GROUP_COLORS.length];
}

// ---- localStorage helpers ----
type GroupMap = { [itemId: string]: { groupId: string; groupName: string } };

function loadGroupMap(workspaceId: string): GroupMap {
    try {
        const raw = localStorage.getItem(`ws-groups-${workspaceId}`);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

function saveGroupMap(workspaceId: string, map: GroupMap) {
    try {
        localStorage.setItem(`ws-groups-${workspaceId}`, JSON.stringify(map));
    } catch { /* storage full – ignore */ }
}

interface IWorkspaceTableProps {
    workspace: string;
    selectedItems: IWorkspaceItem[];
    onSelectedItemsChanged: (items: IWorkspaceItem[]) => void;
    onDetailsClick: (item: IWorkspaceItem) => void;
    onAdhocClick: (item: IWorkspaceItem | undefined) => void;
    onBundleClick?: (item: IWorkspaceItem | undefined) => void;
    onBundleConfigClick?: (item: IWorkspaceItem | undefined) => void;
    onOrderSelectionClick?: (item: IWorkspaceItem | undefined) => void;
    onExternalBundleDrop?: (bundleIds: string[]) => void;
    /** Item ids to outline in the brand color (e.g. lines the prepared cart cannot source). */
    highlightedItemIds?: string[];
}

const WorkspaceTable: React.FC<IWorkspaceTableProps> = (props) => {
    const theme = useTheme();
    const highlightedIds = useMemo(() => new Set(props.highlightedItemIds ?? []), [props.highlightedItemIds]);
    // Every row gets the same wrapper element, and only its style changes. Swapping between a bare row
    // and a wrapped one mid-drag remounted the dragged row's DOM node, which cancels the browser's
    // drag operation: that is why click-and-drag looked dead.
    const onRenderRow: IRenderFunction<IDetailsRowProps> = (rowProps, defaultRender) => {
        if (!rowProps || !defaultRender) return null;
        const item = rowProps.item as IWorkspaceItem | undefined;
        const isDragged = draggedIndex !== undefined && draggedIndex >= 0 && rowProps.itemIndex === draggedIndex;
        const isFlagged = !!item?.id && highlightedIds.has(item.id);
        const style: React.CSSProperties = {
            // The row being dragged fades so it is obvious which line is moving.
            opacity: isDragged ? 0.4 : undefined,
            // Inset so the outline stays inside the row and neighbours do not paint over it.
            outline: isFlagged ? `2px solid ${theme.palette.themePrimary}` : undefined,
            outlineOffset: isFlagged ? -2 : undefined,
            borderRadius: isFlagged ? 2 : undefined,
        };
        return <div style={style}>{defaultRender(rowProps)}</div>;
    };
    const products = useGetWorkspaceProductsQuery(props.workspace, {skip: !props.workspace || props.workspace === ''});
    const [removeProduct] = useRemoveProductFromWorkspaceMutation();
    const [detailsChange] = usePutWorkspaceItemDetailsMutation();
    const getColumns = useGetColumnsQuery();
    const [draggedItem, setDraggedItem] = React.useState<IWorkspaceItem | undefined>();
    const [draggedIndex, setDraggedIndex] = React.useState<number>();
    const [displayProducts, setDisplayProducts] = useState<IWorkspaceItem[]>([]);
    const [isBundleDragOver, setIsBundleDragOver] = React.useState(false);
    const [groupMap, setGroupMap] = useState<GroupMap>({});
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    const [updateGroups] = useUpdateWorkspaceItemGroupingMutation();
    const [updateItemSortGroup] = useUpdateWorkspaceItemGroupingSortMutation();
    const [convertUserAdded] = useConvertUserAddedItemMutation();

    // Stable selection instance — must not be recreated on every render
    const selectionRef = useRef<Selection>(new Selection({
        onSelectionChanged: () => {
            props.onSelectedItemsChanged(selectionRef.current.getSelection() as IWorkspaceItem[]);
        },
    }));

    // Drop target cue: a bold green insertion line along the top of the row you are over.
    const dragEnterClass = mergeStyles({
        backgroundColor: theme.palette.themeLighterAlt,
        boxShadow: `inset 0 3px 0 0 ${theme.palette.themePrimary}`,
    });

    // Load group map from localStorage when workspace changes
    useEffect(() => {
        if (props.workspace) {
            setGroupMap(loadGroupMap(props.workspace));
        }
    }, [props.workspace]);

    // Merge group info onto items and keep displayProducts updated
    useEffect(() => {
        if (products.data) {
            const sorted = [...products.data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            const enriched = sorted.map(item => ({
                ...item,
                groupId: groupMap[item.id]?.groupId,
                groupName: groupMap[item.id]?.groupName,
            }));
            setDisplayProducts(enriched);
        }
    }, [products.data, groupMap]);

    // ---- Group computation -----------------------------------------------
    const USER_ADDED_GROUP_KEY = '__user_added__';
    const USER_ADDED_GROUP_NAME = 'Customer Added';
    const USER_ADDED_GROUP_COLOR = '#040bdc';

    /** Sort: ungrouped (no groupId, not userAdded) → named groups → customer-added.
     *  Within each bucket, sortOrder is preserved from displayProducts (which is already
     *  ordered by sortOrder from the server / drag-drop updates). */
    const sortedItems = useMemo((): IWorkspaceItem[] => {
        // Preserve server sortOrder within each bucket
        const byOrder = [...displayProducts].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const ungrouped = byOrder.filter(i => !i.groupId && !i.userAdded);
        const groupNames = Array.from(
            new Set(byOrder.filter(i => i.groupId && !i.userAdded).map(i => i.groupName ?? ''))
        ).filter(Boolean).sort();
        const grouped = groupNames.flatMap(name =>
            byOrder.filter(i => i.groupName === name && !i.userAdded)
        );
        const userAdded = byOrder.filter(i => i.userAdded);
        return [...ungrouped, ...grouped, ...userAdded];
    }, [displayProducts]);

    const groups = useMemo((): IGroup[] | undefined => {
        const hasNamedGroups = sortedItems.some(i => i.groupId);
        const hasUserAdded = sortedItems.some(i => i.userAdded);
        if (!hasNamedGroups && !hasUserAdded) return undefined;

        const result: IGroup[] = [];
        let idx = 0;

        // Ungrouped items first
        const ungrouped = sortedItems.filter(i => !i.groupId && !i.userAdded);
        if (ungrouped.length > 0) {
            result.push({
                key: '__ungrouped__',
                name: 'Ungrouped',
                startIndex: idx,
                count: ungrouped.length,
                isCollapsed: collapsedGroups.has('__ungrouped__'),
                data: { color: '#888888' },
            });
            idx += ungrouped.length;
        }

        // Named groups
        const groupNames = Array.from(
            new Set(sortedItems.filter(i => i.groupId && !i.userAdded).map(i => i.groupName ?? ''))
        ).filter(Boolean).sort();

        for (const name of groupNames) {
            const count = sortedItems.filter(i => i.groupName === name && !i.userAdded).length;
            const color = colorForGroup(name);
            result.push({
                key: name,
                name,
                startIndex: idx,
                count,
                isCollapsed: collapsedGroups.has(name),
                data: { color },
            });
            idx += count;
        }

        // Customer Added group
        const userAddedItems = sortedItems.filter(i => i.userAdded);
        if (userAddedItems.length > 0) {
            result.push({
                key: USER_ADDED_GROUP_KEY,
                name: USER_ADDED_GROUP_NAME,
                startIndex: idx,
                count: userAddedItems.length,
                isCollapsed: collapsedGroups.has(USER_ADDED_GROUP_KEY),
                data: { color: USER_ADDED_GROUP_COLOR },
            });
        }

        return result.length > 0 ? result : undefined;
    }, [sortedItems, collapsedGroups]);

    // Keep a ref so drag-drop callbacks always see the latest sorted list
    const sortedItemsRef = useRef<IWorkspaceItem[]>([]);
    useEffect(() => {
        sortedItemsRef.current = sortedItems;
    }, [sortedItems]);

    // ---- Group assignment ---------------------------------------------------
    const applyGroup = useCallback((groupName: string | undefined) => {
        const selected = selectionRef.current.getSelection() as IWorkspaceItem[];
        const next: GroupMap = { ...groupMap };

        for (const item of selected) {
            if (groupName) {
                const groupId = groupName.toLowerCase().replace(/\s+/g, '-');
                next[item.id] = { groupId, groupName };
                updateGroups({workspaceId: props.workspace, req: {groupings: [{workspaceItemIds:[item.id], groupName, groupId}]}});
            } else {
                delete next[item.id];
            }
        }

        setGroupMap(next);
        saveGroupMap(props.workspace, next);
        setGroupDialogOpen(false);
    }, [groupMap, props.workspace, updateGroups]);

    // ---- Column header (sticky) ---------------------------------------------
    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (headerProps, defaultRender) => {
        if (!headerProps) return null;
        return (
            <Sticky enabled={true} top={48} innerZ={10}>
                {/* onColumnIsSizingChanged is a header prop, not a list prop: the layout hook uses it to
                    tell real sizer drags from Fluent's own layout passes. */}
                {defaultRender?.({ ...headerProps, onColumnIsSizingChanged })}
            </Sticky>
        );
    };

    // ---- Group header renderer ----------------------------------------------
    const onRenderGroupHeader: IRenderFunction<IDetailsGroupDividerProps> = (groupProps) => {
        if (!groupProps?.group) return null;
        const group = groupProps.group;
        const color: string = group.data?.color ?? '#888';
        const isCollapsed = group.isCollapsed ?? false;

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: `5px solid ${color}`,
                    backgroundColor: `${color}18`,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
                onClick={() =>
                    setCollapsedGroups(prev => {
                        const next = new Set(prev);
                        if (next.has(group.key)) next.delete(group.key);
                        else next.add(group.key);
                        return next;
                    })
                }
            >
                <span style={{
                    fontWeight: 600,
                    color,
                    fontSize: 13,
                    marginRight: 8,
                    minWidth: 16,
                }}>
                    {isCollapsed ? '▶' : '▼'}
                </span>
                <span style={{
                    fontWeight: 600,
                    color,
                    fontSize: 13,
                    letterSpacing: 0.3,
                }}>
                    {group.name}
                </span>
                <span style={{
                    marginLeft: 8,
                    color: '#666',
                    fontSize: 12,
                }}>
                    ({group.count} {group.count === 1 ? 'item' : 'items'})
                </span>
            </div>
        );
    };

    // ---- Columns ------------------------------------------------------------
    // Dragged column widths and drop-reordered columns are remembered in this browser (per table).
    const {listRef, arrange, onColumnResize, onColumnIsSizingChanged, columnReorderOptions, applySavedWidths} = usePersistedColumnLayout('workspaceTable');
    // A grip at the start of every row: most of a row is inputs and dropdowns, where a mouse-down starts
    // editing rather than a drag, so the handle gives an obvious, always-draggable grab point.
    const dragHandleColumn: IColumn = {
        key: 'dragHandle',
        name: '',
        minWidth: 22,
        maxWidth: 22,
        isResizable: false,
        isPadded: false,
        onRender: (item: IWorkspaceItem) => item?.id
            ? <TooltipHost content='Drag to reorder'>
                <Icon iconName='GripperBarVertical' aria-label='Drag to reorder' styles={{root: {cursor: 'grab', color: theme.palette.neutralTertiary, fontSize: 14, lineHeight: '20px', display: 'block', textAlign: 'center'}}} />
            </TooltipHost>
            : null,
    };
    const columns = arrange(renderWorkspaceColumns(
        getColumns.data?.workspaceColumns ?? WORKSPACECOLUMNS,
        false,
        false,
        (product, details) => {
            detailsChange({ workspace: props.workspace, details, product: product.id });
        },
        (item) => {
            removeProduct({ workspaceId: props.workspace, wsItemId: item });
        },
        props.onDetailsClick,
        props.onAdhocClick,
        props.onBundleClick,
        undefined,
        (product) => {
            convertUserAdded({workspaceId: props.workspace, itemId: product.id });
        },
        props.onBundleConfigClick,
        props.onOrderSelectionClick
    ), [dragHandleColumn]).map((column) => {
        // Rows the prepared cart flagged as unsourceable get the same "include anyway" the panel offers,
        // right on the line: it sets the always-available override, the estimate re-runs, the flag clears.
        if (column.key === 'name' || column.fieldName === 'name') {
            // Bundle rows get a peek: hover the eye to see what the bundle holds, click it to open the bundle.
            const renderName = column.onRender;
            return {
                ...column,
                onRender: (item: IWorkspaceItem, index?: number, col?: IColumn) => {
                    const name = renderName?.(item, index, col);
                    if (!item?.isBundle) return name;
                    return (
                        <div style={{display: 'flex', alignItems: 'center', gap: 4, minWidth: 0}}>
                            <div style={{minWidth: 0, flexGrow: 1}}>{name}</div>
                            <BundlePeek item={item} onOpen={props.onBundleClick ? () => props.onBundleClick?.(item) : undefined} />
                        </div>
                    );
                }
            };
        }
        if (column.key !== 'availability' && column.fieldName !== 'availability') return column;
        const renderStock = column.onRender;
        return {
            ...column,
            onRender: (item: IWorkspaceItem, index?: number, col?: IColumn) => {
                // Offer the override whenever the line would not source: the estimate flagged it (panel open),
                // or the product has no vendor in stock at all.
                const flaggedByEstimate = !!item?.id && highlightedIds.has(item.id);
                const noVendorInStock = !item?.isBundle && !!item?.normalized
                    && !(item.normalized.vendors ?? []).some((v) => v.inStock);
                const offerInclude = (flaggedByEstimate || noVendorInStock) && item?.details?.alwaysAvailable !== true;
                const stock = renderStock?.(item, index, col);
                if (!offerInclude) return stock;
                return (
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                        {stock}
                        <TooltipHost content="This line can't be sourced from a vendor right now, so a prepared cart would skip it. Include it anyway; the Always Included toggle undoes this.">
                            <ActionButton
                                iconProps={{iconName: 'Accept'}}
                                styles={{root: {height: 24, padding: 0, color: theme.palette.themePrimary}, icon: {fontSize: 12, color: theme.palette.themePrimary}, label: {fontSize: 12, fontWeight: 600}}}
                                onClick={() => detailsChange({workspace: props.workspace, details: {...item.details, alwaysAvailable: true}, product: item.id})}
                            >
                                Include anyway
                            </ActionButton>
                        </TooltipHost>
                    </div>
                );
            }
        };
    });

    // ---- Drag-drop (internal row reorder) -----------------------------------
    const insertBeforeItem = (draggedItem: IWorkspaceItem, droppedOnItem?: IWorkspaceItem): void => {
        
        
        if(draggedItem.userAdded && droppedOnItem && !droppedOnItem.userAdded) {
            console.log('cannot move customer-added items into a non-customer group');
            return;
        }
        
        let insertIndex = sortedItems.findIndex(i => i.id === droppedOnItem?.id) ?? 0;
        
        if(insertIndex === -1){
            insertIndex = 0
        }

        // Reorder
        const remaining = sortedItems.filter(i => i.id !== draggedItem.id);
        
        remaining.splice(insertIndex, 0, draggedItem);

        // Reassign group membership: dragged items adopt the target's group.
        // Skip if the target is a userAdded item (that group is server-side, not local).
        const nextGroupMap: GroupMap = { ...groupMap };
        let groupChanged = false;



        const targetGroup = groupMap[droppedOnItem?.id ?? '__ungrouped__'];

        const currentGroup = nextGroupMap[draggedItem.id];
        if (targetGroup) {
            if (!currentGroup || currentGroup.groupId !== targetGroup.groupId) {
                nextGroupMap[draggedItem.id] = { groupId: targetGroup.groupId, groupName: targetGroup.groupName };
                groupChanged = true;
            }
        } else {
            if (currentGroup) {
                delete nextGroupMap[draggedItem.id];
                groupChanged = true;
            }
        }
        
        // Merge updated group info back onto remaining items before setting state
        const enriched: IWorkspaceItem[] = remaining.map((item, wsItemIndex) => ({
            ...item,
            sortOrder: (wsItemIndex + 1),
            groupId: nextGroupMap[item.id]?.groupId,
            groupName: nextGroupMap[item.id]?.groupName,
        }));
        
        const updates: IWorkspaceItemGroupSortUpdate[] = []
        forEach(enriched, (item) => {
            const original = displayProducts.find(p => p.id === item.id);
            if (!original || original.sortOrder === item.sortOrder && original.groupId === item.groupId && original.groupName === item.groupName ) return;
            updates.push({sortOrder: item.sortOrder, groupId: item.groupId, groupName: item.groupName, workspaceItemId: item.id});
        })
        if(updates.length === 0) return;

        // Show the new order immediately (the mutation's cache update follows, the server after that),
        // and say so: a reorder used to look like nothing happened until the round trip finished.
        setDisplayProducts(enriched);
        const toastId = toast.loading('Saving item order…', {autoClose: false});
        updateItemSortGroup({workspaceId: props.workspace, req: {updates: updates}}).unwrap()
            .then(() => toast.update(toastId, {type: 'success', render: 'Item order saved', isLoading: false, autoClose: 1500}))
            .catch(() => toast.update(toastId, {type: 'error', render: 'Failed to save the item order', isLoading: false, autoClose: 4000}));
        
        if (groupChanged) {
            setGroupMap(nextGroupMap);
            saveGroupMap(props.workspace, nextGroupMap);
        }
    };

    const getDragDropEvents = () => ({
        canDrop: () => true,
        canDrag: () => true,
        onDragEnter: () => dragEnterClass,
        onDragLeave: () => { return; },
        // Keyed on the workspace item id, not productId: bundles and ad-hoc lines have no productId and
        // could neither be picked up nor dropped onto.
        onDrop: (item: IWorkspaceItem) => {
            if (draggedItem?.id && item?.id && draggedItem.id !== item.id) insertBeforeItem(draggedItem, item);
        },
        onDragStart: (item: IWorkspaceItem, itemIndex?: number) => {
            if(item?.id) {
                setDraggedItem(item);
                setDraggedIndex(itemIndex ?? -1);
            }
        },
        onDragEnd: () => {
            setDraggedItem(undefined);
            setDraggedIndex(-1);
        },
    });

    // ---- Derive current group name from props.selectedItems for the dialog --
    const commonGroupName = props.selectedItems.length > 0
        ? (groupMap[props.selectedItems[0].id]?.groupName ?? '')
        : '';

    // Re-apply remembered widths once the list exists (deduped inside the hook).
    useEffect(() => { applySavedWidths(columns); });

    // ---- Empty workspace (also a drop target) -------------------------------
    // Stays below every hook: returning before one makes the hook count differ between the empty and
    // full renders, and React blanks the page (#300 / #310) when a workspace empties or fills.
    if (props.workspace == null || (!products.isLoading && (products.data == null || products.data.length === 0))) {
        return (
            <div
                onDragOver={(e) => {
                    if (e.dataTransfer.types.includes(BUNDLE_DRAG_TYPE)) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'copy';
                    }
                }}
                onDrop={(e) => {
                    const raw = e.dataTransfer.getData(BUNDLE_DRAG_TYPE);
                    if (raw) {
                        e.preventDefault();
                        try { props.onExternalBundleDrop?.(JSON.parse(raw)); } catch { /* ignore */ }
                    }
                }}
            >
                <Header icon={"FabricFolderFill"}>No Products on Workspace</Header>
                <Stack horizontalAlign='center' styles={{root: {marginTop: '0.5em'}}}>
                    <ActionButton
                        iconProps={{iconName: 'Search'}}
                        title='Search products and add them here without leaving the search (Ctrl+Alt+F)'
                        onClick={() => openGlobalSearch({mode: 'products'})}
                        styles={{root: {height: 28, color: theme.palette.neutralSecondary}, icon: {fontSize: 12, color: theme.palette.neutralSecondary}, label: {fontSize: 12}, rootHovered: {color: theme.palette.themePrimary}, iconHovered: {color: theme.palette.themePrimary}}}
                    >
                        Add something?
                    </ActionButton>
                </Stack>
            </div>
        );
    }

    return (
        <>
            {/* Set Group toolbar */}
            <Stack horizontal tokens={{ childrenGap: 4 }} styles={{ root: { padding: '4px 0 2px 4px' } }}>
                <ActionButton
                    iconProps={{ iconName: 'GroupList' }}
                    text={`Set Group${props.selectedItems.length > 0 ? ` (${props.selectedItems.length})` : ''}`}
                    disabled={props.selectedItems.length === 0 || props.selectedItems.some(i => i.userAdded)}
                    onClick={() => setGroupDialogOpen(true)}
                />
            </Stack>

            {/* Group assignment dialog */}
            <WorkspaceGroupDialog
                isOpen={groupDialogOpen}
                initialGroupName={commonGroupName}
                itemCount={props.selectedItems.length}
                onConfirm={applyGroup}
                onDismiss={() => setGroupDialogOpen(false)}
            />

            {/* Main drop zone */}
            <div
                onDragOver={(e) => {
                    if (e.dataTransfer.types.includes(BUNDLE_DRAG_TYPE)) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'copy';
                        setIsBundleDragOver(true);
                    }
                }}
                onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setIsBundleDragOver(false);
                    }
                }}
                onDrop={(e) => {
                    const raw = e.dataTransfer.getData(BUNDLE_DRAG_TYPE);
                    if (raw) {
                        e.preventDefault();
                        setIsBundleDragOver(false);
                        try { props.onExternalBundleDrop?.(JSON.parse(raw)); } catch { /* ignore */ }
                    }
                }}
                style={isBundleDragOver ? {
                    outline: '3px dashed rgb(120, 156, 47)',
                    borderRadius: 4,
                    backgroundColor: 'rgba(120, 156, 47, 0.07)',
                    transition: 'background 0.15s',
                } : undefined}
            >
                {products.isFetching && <Shimmer />}
                <DetailsList
                    // Same vertical centering as the search grid: every cell's content sits on the row's middle line.
                    className='workspace-table'
                    dragDropEvents={getDragDropEvents()}
                    items={sortedItems}
                    // Rows keyed by item id: after a reorder each row must keep ITS cells (price, presented
                    // price inputs hold local state), not inherit the values of whatever sat in that slot.
                    getKey={(item: IWorkspaceItem) => item.id}
                    groups={groups}
                    columns={columns}
                    selectionMode={SelectionMode.multiple}
                    selection={selectionRef.current}
                    selectionPreservedOnEmptyClick={true}
                    onRenderDetailsHeader={onRenderDetailsHeader}
                    onRenderRow={onRenderRow}
                    onColumnResize={onColumnResize}
                    columnReorderOptions={columnReorderOptions}
                    componentRef={listRef}
                    groupProps={{
                        onRenderHeader: onRenderGroupHeader,
                        showEmptyGroups: false,
                    }}
                />
            </div>
        </>
    );
};

export default WorkspaceTable;